import { authService } from './authService'

const API_URL = process.env.REACT_APP_API_URL ?? 'https://localhost:8443'

const REFRESH_TOKEN_KEY = 'kaab_admin_refresh_token'
const TOKEN_KEY = 'kaab_admin_token'
const USER_KEY = 'kaab_admin_user'

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

async function attemptRefresh(): Promise<string | null> {
  if (isRefreshing) return refreshPromise
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) return null
  isRefreshing = true
  refreshPromise = authService
    .refresh(refreshToken)
    .then(res => {
      localStorage.setItem(TOKEN_KEY, res.token)
      localStorage.setItem(REFRESH_TOKEN_KEY, res.refresh_token)
      if (res.user) localStorage.setItem(USER_KEY, JSON.stringify(res.user))
      window.dispatchEvent(new CustomEvent('auth:refreshed', { detail: { token: res.token } }))
      return res.token
    })
    .catch(() => {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      return null
    })
    .finally(() => {
      isRefreshing = false
      refreshPromise = null
    })
  return refreshPromise
}

class TokenRefreshedSignal extends Error {
  constructor(public readonly newToken: string) {
    super('token_refreshed')
  }
}

const handleResponse = async <T>(response: Response, isRetry = false): Promise<T> => {
  if (response.status === 401 && !isRetry) {
    const newToken = await attemptRefresh()
    if (newToken) throw new TokenRefreshedSignal(newToken)
    window.dispatchEvent(new Event('auth:expired'))
    throw new Error('Unauthorized')
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null as T
  }

  const data = await response.json()

  if (!response.ok) {
    throw data
  }

  return data
}

const API_KEY = process.env.REACT_APP_API_KEY ?? ''

function buildHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function requestWithRetry<T>(
  token: string | null,
  buildRequest: (headers: Record<string, string>) => Promise<Response>
): Promise<T> {
  try {
    const response = await buildRequest(buildHeaders(token))
    return await handleResponse<T>(response)
  } catch (err) {
    if (err instanceof TokenRefreshedSignal) {
      const response = await buildRequest(buildHeaders(err.newToken))
      return handleResponse<T>(response, true)
    }
    throw err
  }
}

export function createApiClient(token: string | null) {
  return {
    get: <T>(path: string): Promise<T> =>
      requestWithRetry<T>(token, headers => fetch(`${API_URL}${path}`, { headers })),

    post: <T>(path: string, body: unknown): Promise<T> =>
      requestWithRetry<T>(token, headers =>
        fetch(`${API_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(body) })
      ),

    put: <T>(path: string, body: unknown): Promise<T> =>
      requestWithRetry<T>(token, headers =>
        fetch(`${API_URL}${path}`, { method: 'PUT', headers, body: JSON.stringify(body) })
      ),

    patch: <T>(path: string, body: unknown): Promise<T> =>
      requestWithRetry<T>(token, headers =>
        fetch(`${API_URL}${path}`, { method: 'PATCH', headers, body: JSON.stringify(body) })
      ),

    delete: <T>(path: string): Promise<T> =>
      requestWithRetry<T>(token, headers =>
        fetch(`${API_URL}${path}`, { method: 'DELETE', headers })
      ),
  }
}
